import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Notifications } from '@projects/adir/src/app/models/notifications.interface';
import { NotificationService } from '@projects/adir/src/app/services/notification.service';
import { CloseIconComponent } from '@shared/src/lib/icons/close-icon/close-icon.component';
import { EpochTimeFormatPipe } from '@shared/src/pipes/epoch-time-format.pipe';
import { NotificationNavigationRequestType } from './notification-dropdown.interface';
import { Router } from '@angular/router';
import { DefaultProfileIconComponent } from '@shared/src/lib/icons/default-profile-icon/default-profile-icon.component';

@Component({
  selector: 'lib-notification-dropdown',
  standalone: true,
  imports: [CommonModule, CloseIconComponent, EpochTimeFormatPipe, DefaultProfileIconComponent],
  templateUrl: './notification-dropdown.component.html',
  styleUrl: './notification-dropdown.component.css',
})
export class NotificationDropdownComponent {
  @Input() notifications: Notifications.Notification[] = [];
  @Input() isUpdating = false;
  @Output() isUpdatingChange = new EventEmitter<boolean>();
  @Output() unreadChanged = new EventEmitter<number>();
  @Output() navigated = new EventEmitter<boolean>();

  get unreadCount() {
    return this.notifications.filter((n) => n.isUnread).length;
  }

  constructor(
    private readonly notificationService: NotificationService,
    private readonly router: Router
  ) {}

  removeNotification(event: Event, index: number) {
    event.stopPropagation();
    event.preventDefault();

    this.setIsUpdating(true);
    this.notificationService.archiveNotifications([this.notifications[index].notificationId]).subscribe({
      next: (res) => {
        this.notifications[index].isActive = false;
        this.notifications.splice(index, 1);
        this.unreadChanged.emit(this.unreadCount);
      },
      error: (err) => {
        console.error(err);
      },
      complete: () => {
        this.setIsUpdating(false);
      },
    });
  }

  markAllAsRead(event: Event) {
    event.preventDefault();
    const notificationIds = this.notifications.filter((n) => n.isUnread).map((n) => n.notificationId);
    this.setIsUpdating(true);
    this.notificationService.markNotificationsAsRead(notificationIds).subscribe({
      next: (res) => {
        this.notifications.forEach((notification) => (notification.isUnread = false));
        this.unreadChanged.emit(this.unreadCount);
      },
      error: (err) => {
        console.error(err);
      },
      complete: () => {
        this.setIsUpdating(false);
      },
    });
  }

  notificationItemClicked(event: Event, notification: Notifications.Notification) {
    event.preventDefault();
    if (notification.isUnread) {
      this.setIsUpdating(true);
      this.notificationService.markNotificationsAsRead([notification.notificationId]).subscribe({
        next: (res) => {
          notification.isUnread = false;
          this.unreadChanged.emit(this.unreadCount);
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.setIsUpdating(false);
          this.redirectToRequest(notification);
        },
      });
    } else {
      this.redirectToRequest(notification);
    }
  }

  redirectToRequest(notification: Notifications.Notification): void {
    // ADIR Request Navigation
    if (
      notification.requestId &&
      notification.requestType === NotificationNavigationRequestType.ADIR &&
      notification.navigation?.adirRequest
    ) {
      const encodedReviewId = encodeURIComponent(notification.navigation.adirRequest.reviewId);
      this.router.navigate([
        'adir',
        notification.requestId,
        encodedReviewId,
        notification.navigation.adirRequest.subModule,
      ]);
      this.navigated.emit(true);
    } else if (
      notification.requestId &&
      notification.requestType === NotificationNavigationRequestType.FDP &&
      notification.navigation?.fdpRequest
    ) {
      const encodedFdpRequestId = encodeURIComponent(notification.requestId);
      this.router.navigate(['fdp', encodedFdpRequestId, notification.navigation.fdpRequest.subModule]);
      this.navigated.emit(true);
    }
  }

  private setIsUpdating(value: boolean): void {
    this.isUpdating = value;
    this.isUpdatingChange.emit(this.isUpdating);
  }
}
